import React, { useState, useEffect, forwardRef,
  useImperativeHandle } from "react";
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { useTranslation } from "../../../../../i18n/useTranslation";
import IdCard from "../../../../../../../../assets/images/PersonIcon.svg";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent, {
  personNameFloatingKeyboardProps,
} from "../../../common/FloatingTextInputComponent";
import {
  isPersonNameSegmentValid,
  sanitizePersonNameInput,
  SENDER_FIRST_NAME_MAX_LENGTH,
  SENDER_LAST_NAME_MAX_LENGTH,
  SENDER_MIDDLE_NAME_MAX_LENGTH,
} from "../../../../../helper/personNameValidation";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string;
};

export interface BillPayEnterNameStepHandle {
  submit: () => Promise<boolean>;
}

const BillPayEnterNameStep = forwardRef<BillPayEnterNameStepHandle, Props>(({ onValidationChange, testID }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
  });
  // display the field label from the gffpFieldsByCategory (from gffp response)
  const gffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).gffpFieldsByCategory);
  const senderInfoFields: any[] = (gffpFieldsByCategory?.["Sender Information"]) || [];
  const getFieldLabel = (tag: string): string | undefined => {
    const field = senderInfoFields.find((f: any) => (f.XmlTag ?? f.xmlTag) === tag);
    return field?.FieldLabel ?? field?.fieldLabel;
  };

  const firstNamePlaceholder = getFieldLabel("senderFirstName") || t("FIRST_NAME");
  const middleNamePlaceholder = getFieldLabel("senderMiddleName") || t("MIDDLE_NAME");
  const lastNamePlaceholder = getFieldLabel("senderLastName") || t("LAST_NAME");
  const [firstName, setFirstName] = useState(() =>
    sanitizePersonNameInput(
      String((useSessionStore.getState() as any).senderFirstName ?? ""),
      SENDER_FIRST_NAME_MAX_LENGTH
    )
  );
  const [middleName, setMiddleName] = useState(() =>
    sanitizePersonNameInput(
      String((useSessionStore.getState() as any).senderMiddleName ?? ""),
      SENDER_MIDDLE_NAME_MAX_LENGTH
    )
  );
  const [lastName, setLastName] = useState(() =>
    sanitizePersonNameInput(
      String((useSessionStore.getState() as any).senderLastName ?? ""),
      SENDER_LAST_NAME_MAX_LENGTH
    )
  );
  const setSenderFirstName = useSessionStore((s: SessionState) => (s as any).setSenderFirstName);
  const setSenderMiddleName = useSessionStore((s: SessionState) => (s as any).setSenderMiddleName);
  const setSenderLastName = useSessionStore((s: SessionState) => (s as any).setSenderLastName);

  const isValid =
    isPersonNameSegmentValid(firstName, true, SENDER_FIRST_NAME_MAX_LENGTH) &&
    isPersonNameSegmentValid(middleName, false, SENDER_MIDDLE_NAME_MAX_LENGTH) &&
    isPersonNameSegmentValid(lastName, true, SENDER_LAST_NAME_MAX_LENGTH);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // expose validate function
  useImperativeHandle(ref, () => ({
    submit: async () => {
      const firstErr = !firstName.trim()
        ? t("PERSON_NAME_ERROR_FIRST_REQUIRED")
        : firstName.length > SENDER_FIRST_NAME_MAX_LENGTH
          ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: SENDER_FIRST_NAME_MAX_LENGTH })
          : !isPersonNameSegmentValid(firstName, true)
            ? t("PERSON_NAME_ERROR_LETTERS_ONLY")
            : "";
      const lastErr = !lastName.trim()
        ? t("PERSON_NAME_ERROR_LAST_REQUIRED")
        : lastName.length > SENDER_LAST_NAME_MAX_LENGTH
          ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: SENDER_LAST_NAME_MAX_LENGTH })
          : !isPersonNameSegmentValid(lastName, true)
            ? t("PERSON_NAME_ERROR_LETTERS_ONLY")
            : "";
      const middleErr =
        middleName.trim() &&
        (middleName.length > SENDER_MIDDLE_NAME_MAX_LENGTH
          ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: SENDER_MIDDLE_NAME_MAX_LENGTH })
          : !isPersonNameSegmentValid(middleName, false)
            ? t("PERSON_NAME_ERROR_LETTERS_ONLY")
            : "");
      const newErrors = { firstName: firstErr, middleName: middleErr, lastName: lastErr };
      setErrors(newErrors);

      return !Object.values(newErrors).some(Boolean);
    },
  }));

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      testID={testID}
    >
      <Text style={styles.title}>{t("SENDER_NAME_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_NAME_SUBTITLE")}</Text>
      <FloatingTextInputComponent
        {...personNameFloatingKeyboardProps}
        testID="billPayFirstName"
        label={firstNamePlaceholder}
        value={firstName}
        isAutoFocus={false}
        maxLength={SENDER_FIRST_NAME_MAX_LENGTH}
        onChangeText={(val) => {
          const s = sanitizePersonNameInput(val, SENDER_FIRST_NAME_MAX_LENGTH);
          setFirstName(s);
          setSenderFirstName?.(s);
        }}
        errorText={errors.firstName}
        onClearError={() => setErrors((prev) => ({ ...prev, firstName: "" }))}
        blockPersonNameAutofill
      />
      <FloatingTextInputComponent
        {...personNameFloatingKeyboardProps}
        testID="billPayMiddleName"
        label={middleNamePlaceholder}
        value={middleName}
        maxLength={SENDER_MIDDLE_NAME_MAX_LENGTH}
        onChangeText={(v) => {
          const s = sanitizePersonNameInput(v, SENDER_MIDDLE_NAME_MAX_LENGTH);
          setMiddleName(s);
          try {
            setSenderMiddleName?.(s);
          } catch {}
        }}
        errorText={errors.middleName}
        onClearError={() => setErrors((prev) => ({ ...prev, middleName: "" }))}
        blockPersonNameAutofill
      />
      <FloatingTextInputComponent
        {...personNameFloatingKeyboardProps}
        testID="billPayLastName"
        label={lastNamePlaceholder}
        value={lastName}
        maxLength={SENDER_LAST_NAME_MAX_LENGTH}
        onChangeText={(val) => {
          const s = sanitizePersonNameInput(val, SENDER_LAST_NAME_MAX_LENGTH);
          setLastName(s);
          setSenderLastName?.(s);
        }}
        errorText={errors.lastName}
        onClearError={() => setErrors((prev) => ({ ...prev, lastName: "" }))}
        blockPersonNameAutofill
      />
      {/* Hint box */}
      <View style={styles.hintBox}>
        <IdCard width={24} height={24}/>
        <Text style={styles.hintText}>
          {t("MAKE_SURE_TO_USE_NAME_ON_YOUR_LEGAL_ID")}
        </Text>
      </View>
    </KeyboardAvoidingView>
  );
});

export default BillPayEnterNameStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  hintBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 10,
    padding: 16,
    marginTop: 24,
    backgroundColor: Colors.white,
    marginBottom: 79
  },
  hintText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: '400', 
    color: Colors.darkGrey,
    lineHeight: 18,
    paddingLeft: 16
  },
});